import React, { useEffect, useRef, useState } from 'react';

// Reusable grade hero ring: tone-colored SVG arc + a letter grade and a
// score that counts up once on reveal. Purely presentational.
const R = 54;
const C = 2 * Math.PI * R;

const prefersReduced =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const GradeRing = ({ score = 0, letter = '-', tone = 'fair' }) => {
  const target = Math.max(0, Math.min(100, Math.round(Number(score) || 0)));
  const [display, setDisplay] = useState(prefersReduced ? target : 0);
  const [offset, setOffset] = useState(prefersReduced ? C * (1 - target / 100) : C);
  const rafRef = useRef(0);

  useEffect(() => {
    if (prefersReduced) {
      setDisplay(target);
      setOffset(C * (1 - target / 100));
      return undefined;
    }

    // Kick the ring fill on the next frame so the CSS transition runs.
    const fillId = requestAnimationFrame(() => setOffset(C * (1 - target / 100)));

    // Count the number up once.
    const duration = 760;
    let startTs = null;
    const tick = (ts) => {
      if (startTs === null) startTs = ts;
      const p = Math.min(1, (ts - startTs) / duration);
      setDisplay(Math.round(target * easeOutCubic(p)));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(fillId);
      cancelAnimationFrame(rafRef.current);
    };
  }, [target]);

  return (
    <div
      className="vlg-grade__ring"
      data-tone={tone}
      role="img"
      aria-label={`Grade ${letter}, ${target} out of 100`}
    >
      <svg viewBox="0 0 120 120">
        <circle className="vlg-gring__track" cx="60" cy="60" r={R} />
        <circle
          className="vlg-gring__fill"
          cx="60"
          cy="60"
          r={R}
          strokeDasharray={C}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="vlg-grade__center">
        <span className="vlg-grade__letter">{letter}</span>
        <span className="vlg-grade__num"><b>{display}</b>/100</span>
      </div>
    </div>
  );
};

export default GradeRing;
